import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import './CheckpointInterface.css';
import PhotoUpload from './PhotoUpload';

/**
 * HAKLAI CHURCH — CHECKPOINT (React Component)
 * Unifica a lógica de marcação de presença com o design premium e criação on-the-fly de eventos.
 */
function CheckpointInterface({ cell, event, members = [], initialData = null, onSubmit, onBack, loading = false }) {
    // ESTRUTURAS DE DADOS PADRÃO PARA EVENTO
    const getDefaultDate = () => {
        const d = new Date();
        return d.toISOString().split('T')[0];
    };

    const getDefaultTime = () => {
        const d = new Date();
        return d.toTimeString().substring(0, 5);
    };

    const queryClient = useQueryClient();

    // ESTADOS
    const [eventData, setEventData] = useState({
        type: event?.event_type || 'Reunião de Célula',
        date: event?.event_date ? event.event_date.split('T')[0] : getDefaultDate(),
        time: event?.start_time ? event.start_time.substring(0, 5) : getDefaultTime(),
        description: event?.description || ''
    });

    const [statuses, setStatuses] = useState({}); // { memberId: 'present' | 'absent' }
    const [justifications, setJustifications] = useState({}); // { memberId: { type, text } }
    const [visitors, setVisitors] = useState([]); // [ { name, phone, email, photo_base64 } ]
    const [notes, setNotes] = useState('');
    
    // Modais
    const [justifModal, setJustifModal] = useState({ open: false, member: null, type: 'outro', text: '' });
    const [visitorModal, setVisitorModal] = useState({ open: false, name: '', phone: '', email: '', photoFile: null });
    const [memberModal, setMemberModal] = useState({ open: false, name: '', phone: '', photoFile: null });
    const [isAddingMember, setIsAddingMember] = useState(false);
    const [successOverlay, setSuccessOverlay] = useState(false);

    // EFEITO: Inicializar com dados existentes
    useEffect(() => {
        if (initialData && initialData.attendances) {
            const newStatuses = {};
            const newJustifs = {};
            initialData.attendances.forEach(att => {
                newStatuses[att.member_id] = att.status;
                if (att.justification) {
                    const parts = att.justification.split(': ');
                    if (parts.length > 1) {
                        newJustifs[att.member_id] = { type: parts[0].toLowerCase(), text: parts[1] };
                    } else {
                        newJustifs[att.member_id] = { type: 'outro', text: att.justification };
                    }
                }
            });
            setStatuses(newStatuses);
            setJustifications(newJustifs);
            if (initialData.notes) setNotes(initialData.notes);
            if (initialData.visitors) setVisitors(initialData.visitors);
        }
    }, [initialData]);

    // HANDLERS
    const handleMemberClick = (member) => {
        const currentStatus = statuses[member.id];

        if (!currentStatus || currentStatus === 'absent') {
            setStatuses(prev => ({ ...prev, [member.id]: 'present' }));
            setJustifications(prev => {
                const updated = { ...prev };
                delete updated[member.id];
                return updated;
            });
        } else if (currentStatus === 'present') {
            setJustifModal({
                open: true,
                member: member,
                type: justifications[member.id]?.type || 'outro',
                text: justifications[member.id]?.text || ''
            });
        }
    };

    const confirmJustification = () => {
        const { member, type, text } = justifModal;
        setStatuses(prev => ({ ...prev, [member.id]: 'absent' }));
        setJustifications(prev => ({
            ...prev,
            [member.id]: { type, text }
        }));
        setJustifModal({ open: false, member: null, type: 'outro', text: '' });
    };

    const fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

    const addVisitor = async () => {
        if (visitorModal.name.trim()) {
            let photo_base64 = null;
            if (visitorModal.photoFile) {
                photo_base64 = await fileToBase64(visitorModal.photoFile);
            }

            setVisitors(prev => [...prev, { 
                name: visitorModal.name, 
                phone: visitorModal.phone, 
                email: visitorModal.email,
                photo_base64: photo_base64
            }]);
            setVisitorModal({ open: false, name: '', phone: '', email: '', photoFile: null });
        }
    };

    const removeVisitor = (index) => {
        setVisitors(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddMember = async () => {
        if (!memberModal.name.trim()) return;
        setIsAddingMember(true);
        try {
            // 1. Criar membro na base de dados
            const response = await api.post('/members', {
                full_name: memberModal.name,
                phone: memberModal.phone,
                category: 'member',
                cell_id: cell.id
            });
            const newMemberId = response.data.data.id;

            // 2. Fazer upload da foto, se existir
            if (memberModal.photoFile) {
                const photoFormData = new FormData();
                photoFormData.append('photo', memberModal.photoFile);
                await api.post(`/members/${newMemberId}/photo`, photoFormData);
            }

            // 3. Atualizar cache para refletir na lista
            await queryClient.invalidateQueries({ queryKey: ['cell-members', String(cell.id)] });
            
            // Marcar automaticamente como presente? O usuário precisará clicar, mas estará na lista
            setMemberModal({ open: false, name: '', phone: '', photoFile: null });
        } catch (error) {
            alert('Erro ao criar membro: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsAddingMember(false);
        }
    };

    const handleFinalSubmit = () => {
        const report = {
            cell_id: cell.id,
            eventData: eventData, // Enviamos os dados do evento para a Mutation criar
            notes,
            visitors,
            attendances: members.map(m => {
                const status = statuses[m.id] || 'absent';
                const justif = justifications[m.id];
                return {
                    member_id: m.id,
                    status: status,
                    justification: status === 'absent' 
                        ? (justif ? `${justif.type.charAt(0).toUpperCase() + justif.type.slice(1)}: ${justif.text}` : 'Ausência não justificada')
                        : null
                };
            })
        };

        onSubmit(report, () => {
            setSuccessOverlay(true);
            setTimeout(() => {
                setSuccessOverlay(false);
            }, 3000);
        });
    };

    // CALCULOS
    const stats = {
        present: Object.values(statuses).filter(s => s === 'present').length,
        absent: Object.values(statuses).filter(s => s === 'absent').length,
        pending: members.length - Object.keys(statuses).length
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    // Estilos Inline Específicos para o Formulário de Evento para manter design apurado
    const eventFormStyle = {
        background: 'var(--bg-card)',
        padding: '20px',
        borderRadius: '16px',
        border: '1px solid var(--border-color)',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    };

    return (
        <div className="checkpoint-interface">
            {/* Header */}
            <header className="cp-header">
                <div className="cp-header-top">
                    <button className="cp-back-btn" onClick={onBack}>
                        <span className="material-symbols-outlined">arrow_back</span>
                    </button>
                    <span className="cp-header-title">Checkpoint de Célula</span>
                    <div style={{ width: 40 }}></div>
                </div>
                <div className="cp-header-info">
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>home_work</span>
                    <strong>{cell?.name || 'Carregando...'}</strong>
                    <span>•</span>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>calendar_today</span>
                    {new Date(eventData.date).toLocaleDateString('pt-BR')}
                </div>
            </header>

            {/* Stats Summary */}
            <div className="cp-stats-summary">
                <div className="cp-summary-stat present">
                    <div className="cp-stat-number">{stats.present}</div>
                    <div className="cp-stat-text">Presentes</div>
                </div>
                <div className="cp-summary-stat absent">
                    <div className="cp-stat-number">{stats.absent}</div>
                    <div className="cp-stat-text">Ausentes</div>
                </div>
                <div className="cp-summary-stat pending">
                    <div className="cp-stat-number">{stats.pending}</div>
                    <div className="cp-stat-text">Pendentes</div>
                </div>
            </div>

            {/* Content */}
            <main className="cp-content">
                {/* Event Form On-The-Fly */}
                {!event && (
                    <div style={eventFormStyle}>
                        <h2 className="cp-section-title" style={{ marginBottom: 16 }}>Detalhes do Evento</h2>
                        
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Tipo de Evento *</label>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                {['Reunião de Célula', 'Reunião de Líderes', 'Outro'].map(type => (
                                    <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', cursor: 'pointer' }}>
                                        <input 
                                            type="radio" 
                                            name="eventType" 
                                            value={type} 
                                            checked={eventData.type === type}
                                            onChange={(e) => setEventData({...eventData, type: e.target.value})}
                                            style={{ accentColor: 'var(--color-primary)' }}
                                        />
                                        {type}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Data *</label>
                                <input 
                                    type="date" 
                                    className="cp-notes-input" 
                                    value={eventData.date}
                                    onChange={(e) => setEventData({...eventData, date: e.target.value})}
                                    required
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Horário de Início</label>
                                <input 
                                    type="time" 
                                    className="cp-notes-input" 
                                    value={eventData.time}
                                    onChange={(e) => setEventData({...eventData, time: e.target.value})}
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Descrição Opcional</label>
                            <textarea 
                                className="cp-notes-input"
                                placeholder="Tema ou anotações rápidas sobre o evento..."
                                value={eventData.description}
                                onChange={(e) => setEventData({...eventData, description: e.target.value})}
                                rows={2}
                            />
                        </div>
                    </div>
                )}

                <h2 className="cp-section-title">Marcação de Presença</h2>
                <p className="cp-section-hint">Toque uma vez para presença, toque novamente para ausência.</p>

                <div className="cp-member-list">
                    {members.map(member => {
                        const status = statuses[member.id];
                        return (
                            <div 
                                key={member.id} 
                                className={`cp-member-card ${status || ''}`}
                                onClick={() => handleMemberClick(member)}
                            >
                                <div className="cp-member-photo">
                                    {member.photo_thumbnail_url ? (
                                        <img src={member.photo_thumbnail_url} alt={member.full_name} />
                                    ) : (
                                        <div className="cp-member-initials">{getInitials(member.full_name)}</div>
                                    )}
                                </div>
                                <div className="cp-member-info">
                                    <div className="cp-member-name">{member.full_name}</div>
                                    <div className="cp-member-status-text">
                                        {!status ? 'Toque para marcar' : status === 'present' ? 'Presente' : 'Ausente'}
                                        {status === 'absent' && justifications[member.id]?.text && ` (${justifications[member.id].text})`}
                                    </div>
                                </div>
                                <div className="cp-status-icon">
                                    <span className="material-symbols-outlined">
                                        {!status ? 'radio_button_unchecked' : status === 'present' ? 'check_circle' : 'cancel'}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Visitantes */}
                <h2 className="cp-section-title" style={{ marginTop: 32 }}>Visitantes ({visitors.length})</h2>
                <p className="cp-section-hint">Adicione pessoas que não são membros regulares da célula.</p>
                
                <div className="cp-member-list">
                    {visitors.map((v, idx) => (
                        <div key={idx} className="cp-member-card present" style={{ borderColor: 'var(--color-visitor)' }}>
                            <div className="cp-member-photo" style={{ borderColor: 'var(--color-visitor)' }}>
                                {v.photo_base64 ? (
                                    <img src={v.photo_base64} alt={v.name} />
                                ) : (
                                    <div className="cp-member-initials" style={{ background: 'var(--color-visitor)', color: 'white' }}>
                                        {getInitials(v.name)}
                                    </div>
                                )}
                            </div>
                            <div className="cp-member-info">
                                <div className="cp-member-name">{v.name}</div>
                                <div className="cp-member-status-text" style={{ color: 'var(--color-visitor)' }}>Novo Visitante</div>
                            </div>
                            <button 
                                className="cp-status-icon" 
                                style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); removeVisitor(idx); }}
                            >
                                <span className="material-symbols-outlined">delete</span>
                            </button>
                        </div>
                    ))}
                    
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                        <button 
                            className="cp-add-btn" 
                            onClick={() => setVisitorModal({ ...visitorModal, open: true })}
                            style={{ flex: 1, minWidth: 'max-content' }}
                        >
                            <span className="material-symbols-outlined">person_add</span>
                            Adicionar Visitante
                        </button>

                        <button 
                            className="cp-add-btn" 
                            onClick={() => setMemberModal({ ...memberModal, open: true })}
                            style={{ flex: 1, minWidth: 'max-content', background: 'var(--checkpoint-card)', color: 'var(--checkpoint-member)', borderColor: 'var(--checkpoint-member)', borderStyle: 'solid', borderWidth: '1px' }}
                        >
                            <span className="material-symbols-outlined">person_add_alt_1</span>
                            Adicionar Membro
                        </button>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="cp-footer">
                <textarea 
                    className="cp-notes-input"
                    placeholder="Observações do encontro (opcional)..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                />
                <button 
                    className="cp-submit-btn" 
                    onClick={handleFinalSubmit}
                    disabled={loading || stats.pending > 0}
                    style={{ opacity: (loading || stats.pending > 0) ? 0.7 : 1 }}
                >
                    {loading ? (
                        <>Processando...</>
                    ) : (
                        <>
                            <span className="material-symbols-outlined">send</span>
                            Enviar Relatório de Célula
                        </>
                    )}
                </button>
                {stats.pending > 0 && (
                    <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--color-danger)', marginTop: 8 }}>
                        Marque todos os membros para enviar o relatório.
                    </p>
                )}
            </footer>

            {/* Modal Justificativa */}
            {justifModal.open && (
                <div className="cp-modal-overlay active" onClick={() => setJustifModal({ ...justifModal, open: false })}>
                    <div className="cp-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="cp-modal-handle"></div>
                        <h3 className="cp-modal-title">Justificativa: {justifModal.member?.full_name}</h3>
                        <div className="cp-justification-grid">
                            {['doenca', 'viagem', 'trabalho', 'outro'].map(t => (
                                <div 
                                    key={t}
                                    className={`cp-justification-opt ${justifModal.type === t ? 'selected' : ''}`}
                                    onClick={() => setJustifModal({ ...justifModal, type: t })}
                                >
                                    <span className="option-label">{t.charAt(0).toUpperCase() + t.slice(1)}</span>
                                </div>
                            ))}
                        </div>
                        <textarea 
                            className="cp-notes-input"
                            placeholder="Mais detalhes (opcional)..."
                            value={justifModal.text}
                            onChange={(e) => setJustifModal({ ...justifModal, text: e.target.value })}
                        />
                        <div className="cp-modal-actions">
                            <button className="cp-modal-btn secondary" onClick={() => setJustifModal({ ...justifModal, open: false })}>Cancelar</button>
                            <button className="cp-modal-btn primary" onClick={confirmJustification}>Confirmar Ausência</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Visitante */}
            {visitorModal.open && (
                <div className="cp-modal-overlay active" onClick={() => setVisitorModal({ ...visitorModal, open: false })}>
                    <div className="cp-modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="cp-modal-handle"></div>
                        <h3 className="cp-modal-title">Adicionar Visitante</h3>
                        
                        <div style={{ marginBottom: 20 }}>
                            <PhotoUpload 
                                memberId={null} 
                                onFileSelect={(file) => setVisitorModal({ ...visitorModal, photoFile: file })}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                            <input 
                                className="cp-notes-input" 
                                placeholder="Nome Completo *" 
                                value={visitorModal.name}
                                onChange={(e) => setVisitorModal({ ...visitorModal, name: e.target.value })}
                            />
                            <input 
                                className="cp-notes-input" 
                                placeholder="Telefone" 
                                value={visitorModal.phone}
                                onChange={(e) => setVisitorModal({ ...visitorModal, phone: e.target.value })}
                            />
                            <input 
                                className="cp-notes-input" 
                                placeholder="E-mail" 
                                value={visitorModal.email}
                                onChange={(e) => setVisitorModal({ ...visitorModal, email: e.target.value })}
                            />
                        </div>
                        <div className="cp-modal-actions">
                            <button className="cp-modal-btn secondary" onClick={() => setVisitorModal({ ...visitorModal, open: false, photoFile: null })}>Cancelar</button>
                            <button className="cp-modal-btn primary" onClick={addVisitor} disabled={!visitorModal.name.trim()}>Adicionar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Membro */}
            {memberModal.open && (
                <div className="cp-modal-overlay active" onClick={() => !isAddingMember && setMemberModal({ ...memberModal, open: false })}>
                    <div className="cp-modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="cp-modal-handle"></div>
                        <h3 className="cp-modal-title">Adicionar Novo Membro</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--checkpoint-muted)', marginBottom: 20, textAlign: 'center' }}>
                            Este membro será gravado permanentemente na base de dados e adicionado à célula <strong>{cell?.name}</strong>.
                        </p>
                        
                        <div style={{ marginBottom: 20 }}>
                            <PhotoUpload 
                                memberId={null} 
                                onFileSelect={(file) => setMemberModal({ ...memberModal, photoFile: file })}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                            <input 
                                className="cp-notes-input" 
                                placeholder="Nome Completo *" 
                                value={memberModal.name}
                                onChange={(e) => setMemberModal({ ...memberModal, name: e.target.value })}
                                disabled={isAddingMember}
                            />
                            <input 
                                className="cp-notes-input" 
                                placeholder="Telefone" 
                                value={memberModal.phone}
                                onChange={(e) => setMemberModal({ ...memberModal, phone: e.target.value })}
                                disabled={isAddingMember}
                            />
                        </div>
                        <div className="cp-modal-actions">
                            <button className="cp-modal-btn secondary" onClick={() => setMemberModal({ ...memberModal, open: false, photoFile: null })} disabled={isAddingMember}>Cancelar</button>
                            <button className="cp-modal-btn primary" onClick={handleAddMember} disabled={!memberModal.name.trim() || isAddingMember}>
                                {isAddingMember ? 'Gravando...' : 'Adicionar Membro'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Overlay */}
            {successOverlay && (
                <div className="cp-success-overlay active">
                    <div className="cp-success-icon">
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                    <div className="cp-success-title">Checkpoint Enviado!</div>
                    <div className="cp-section-hint">
                        {stats.present} presentes • {stats.absent} ausentes
                    </div>
                </div>
            )}
        </div>
    );
}

export default CheckpointInterface;
